import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UserType = 'creator' | 'joiner' | null;

export interface OnboardingSteps {
  userType: UserType;
  complete: boolean;
  dismissedAt: string | null;
  templateApplied: string | null;
  // creator steps
  createHousehold: boolean;
  inviteMember: boolean;
  addThreeTasks: boolean;
  addCalendarEvent: boolean;
  setupGroceryCategories: boolean;
  addMaintenanceItem: boolean;
  // joiner steps
  joinHousehold: boolean;
  createFirstTask: boolean;
}

export type MarkableStep =
  | 'createHousehold'
  | 'inviteMember'
  | 'addThreeTasks'
  | 'addCalendarEvent'
  | 'setupGroceryCategories'
  | 'addMaintenanceItem'
  | 'joinHousehold'
  | 'createFirstTask';

export interface OnboardingContextValue {
  steps: OnboardingSteps;
  isComplete: boolean;
  loading: boolean;
  markStep: (step: MarkableStep) => Promise<void>;
  setUserType: (type: UserType) => Promise<void>;
  setTemplateApplied: (template: string) => Promise<void>;
  dismissPermanently: () => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DEFAULT_STEPS: OnboardingSteps = {
  userType: null,
  complete: false,
  dismissedAt: null,
  templateApplied: null,
  createHousehold: false,
  inviteMember: false,
  addThreeTasks: false,
  addCalendarEvent: false,
  setupGroceryCategories: false,
  addMaintenanceItem: false,
  joinHousehold: false,
  createFirstTask: false,
};

function computeIsComplete(steps: OnboardingSteps): boolean {
  if (steps.complete) return true;
  if (steps.userType === 'creator') {
    return (
      steps.createHousehold &&
      steps.inviteMember &&
      steps.addThreeTasks &&
      steps.addCalendarEvent &&
      steps.setupGroceryCategories &&
      steps.addMaintenanceItem
    );
  }
  if (steps.userType === 'joiner') {
    return steps.joinHousehold && steps.createFirstTask;
  }
  return false;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const OnboardingProvider: React.FC<{
  userId: string | null;
  children: React.ReactNode;
}> = ({ userId, children }) => {
  const [steps, setSteps] = useState<OnboardingSteps>(DEFAULT_STEPS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const ref = doc(db, 'users', userId, 'onboarding', 'state');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as Record<string, any>;
        // Backward compat: old docs (inviteMembers + createFirstTask + setPreferences) resolve to complete
        // Only applies to pre-wizard users who have no userType set
        const legacyComplete =
          !data.userType &&
          data.inviteMembers === true &&
          (data.createFirstTask === true || data.createFirstTaskOld === true) &&
          data.setPreferences === true;
        const merged: OnboardingSteps = {
          ...DEFAULT_STEPS,
          ...(data as Partial<OnboardingSteps>),
          complete: data.complete === true || legacyComplete,
        };
        setSteps(merged);
      } else {
        setSteps(DEFAULT_STEPS);
      }
      setLoading(false);
    });

    return unsub;
  }, [userId]);

  const savePartial = useCallback(
    async (partial: Partial<OnboardingSteps> & Record<string, any>) => {
      if (!userId) return;
      const ref = doc(db, 'users', userId, 'onboarding', 'state');
      await setDoc(ref, { ...partial, updatedAt: serverTimestamp() }, { merge: true });
    },
    [userId]
  );

  const markStep = useCallback(
    async (step: MarkableStep) => {
      if (!userId) return;
      const updated = { ...steps, [step]: true };
      const isNowComplete = computeIsComplete(updated);
      await savePartial({ [step]: true, ...(isNowComplete ? { complete: true } : {}) });
    },
    [steps, savePartial, userId]
  );

  const setUserType = useCallback(
    async (type: UserType) => {
      await savePartial({ userType: type });
    },
    [savePartial]
  );

  const setTemplateApplied = useCallback(
    async (template: string) => {
      await savePartial({ templateApplied: template });
    },
    [savePartial]
  );

  const dismissPermanently = useCallback(async () => {
    await savePartial({ complete: true, dismissedAt: new Date().toISOString() });
  }, [savePartial]);

  const isComplete = computeIsComplete(steps);

  return (
    <OnboardingContext.Provider
      value={{ steps, isComplete, loading, markStep, setUserType, setTemplateApplied, dismissPermanently }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    // Null-safe fallback so components don't crash if mounted outside OnboardingProvider
    return {
      steps: DEFAULT_STEPS,
      isComplete: true, // treat as complete → checklist widget won't render
      loading: false,
      markStep: async () => {},
      setUserType: async () => {},
      setTemplateApplied: async () => {},
      dismissPermanently: async () => {},
    };
  }
  return ctx;
}
